/* eslint-disable indent */
'use client';

import React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Calendar,
  Clock,
  User,
  ArrowLeft,
  Share2,
  Bookmark,
  ChevronRight,
  BookmarkCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useRouter } from 'next/navigation';
import { authService } from '@/services/auth.service';

import Loading from '@/app/loading';

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'has',
  'in',
  'is',
  'it',
  'its',
  'of',
  'on',
  'that',
  'the',
  'to',
  'was',
  'were',
  'with',
  'this',
  'or',
  'but',
  'so',
  'if',
  'then',
  'there',
  'here',
  'when',
  'where',
]);

// Format date
const formatDate = (dateString) => {
  // Handle date format like "18/05/2025"
  if (dateString.includes('/')) {
    const [day, month, year] = dateString.split('/');
    return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Handle standard date format
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

export default function ArticleClientPage({ params }) {
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();

  const fetchArticles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await authService.getArticles(); // fetch all for related articles
      if (response?.data?.items?.length) {
        return response.data.items;
      }
      return [];
    } catch (error) {
      console.error('Error fetching articles:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchArticleBySlug = useCallback(
    async (slug) => {
      try {
        setIsLoading(true);
        
        // Fetch all articles to get ID
        const allArticles = await fetchArticles();

        if (!allArticles || allArticles.length === 0) {
          router.replace('/404');
          return;
        }

        // if (!allArticles || allArticles.length === 0) {
        //   setArticle(null);
        //   setRelatedArticles([]);
        //   return;
        // }

        // Find the article by slug
        const articleData = allArticles.find((a) => a.slug === slug);

        if (!articleData?._id) {
          router.replace('/404');
          return;
        }

        // if (!articleData?._id) {
        //   console.warn('Article not found for slug:', slug);
        //   setArticle(null);
        //   setRelatedArticles([]);
        //   return;
        // }

        // Fetch detailed article by ID
        const response = await authService.getArticlesById(articleData._id);
        const detailedArticle = response?.data?.item || null;

        if (!detailedArticle) {
          router.replace('/404');
          return;
        }

        // if (!detailedArticle) {
        //   console.warn('Detailed article not found for ID:', articleData._id);
        //   setArticle(null);
        //   setRelatedArticles([]);
        //   return;
        // }

        // Set the main article
        setArticle(detailedArticle);

        // Related articles: same category, different ID
        const related = allArticles
          .filter(
            (item) =>
              item?._id !== detailedArticle._id &&
              item?.category === detailedArticle?.category,
          )
          .sort((a, b) => (b?.sorting || 0) - (a?.sorting || 0)); // safe numeric sort

        setRelatedArticles(related || []);

        // Bookmarks
        const bookmarks = JSON.parse(
          localStorage.getItem('xspace_bookmarks') || '[]',
        );
        setIsBookmarked(bookmarks.some((b) => b.slug === slug));
      } catch (error) {
        // console.error('Error fetching article by slug:', error);
        // setArticle(null);
        // setRelatedArticles([]);
        console.error('Error fetching article by slug:', error);
        router.replace('/404');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchArticles, router],
  );

  const categories = useMemo(() => {
    const rawKeywords = article?.meta_keyword || '';

    if (rawKeywords.includes(',')) {
      return rawKeywords
        .split(',')
        .map((kw) => kw.trim())
        .filter((kw) => kw.length > 0);
    } else {
      return rawKeywords
        .split(' ')
        .map((word) => word.trim().toLowerCase())
        .filter(
          (word) => word && !STOP_WORDS.has(word) && /^[a-z]+$/.test(word),
        );
    }
  }, [article?.meta_keyword]);

  useEffect(() => {
    fetchArticleBySlug(params.slug);
  }, [params.slug, fetchArticleBySlug]);

  // Handle bookmarking
  const toggleBookmark = () => {
    if (!article) return;

    const bookmarks = JSON.parse(
      localStorage.getItem('xspace_bookmarks') || '[]',
    );

    if (isBookmarked) {
      // Remove bookmark
      const updatedBookmarks = bookmarks.filter(
        (bookmark) => bookmark.slug !== article?.slug,
      );
      localStorage.setItem(
        'xspace_bookmarks',
        JSON.stringify(updatedBookmarks),
      );
      setIsBookmarked(false);
    } else {
      // Add bookmark
      const newBookmark = {
        id: article?._id,
        title: article?.title,
        slug: article?.slug,
        image: article?.image_url,
        date: article?.date,
        category: article?.category,
        author: article?.author,
      };
      localStorage.setItem(
        'xspace_bookmarks',
        JSON.stringify([...bookmarks, newBookmark]),
      );
      setIsBookmarked(true);
    }
  };

  // Function to navigate to bookmarks page
  const goToBookmarks = () => {
    router.push('/bookmarks');
  };

  if (isLoading || !article) {
    return <Loading />;
  }

  // Extract categories from meta keywords
  // const categories = rawKeywords.includes(',')
  //   ? rawKeywords
  //       .split(',')
  //       .map((kw) => kw.trim())
  //       .filter((kw) => kw.length > 0) // keep valid phrases
  //   : rawKeywords
  //       .split(' ')
  //       .map((word) => word.trim().toLowerCase())
  //       .filter(
  //         (word) => word && !STOP_WORDS.has(word) && /^[a-z]+$/.test(word)
  //       );
  // // .slice(0, 5) || [];

  // Function to share article
  const shareUrl = `https://www.x-space.ae/articles/${article?.slug}`;

  const shareArticle = () => {
    if (navigator.share) {
      navigator
        .share({
          title: article?.title,
          text: article?.meta_description,
          url: shareUrl,
        })
        .catch((err) => {
          console.error('Error sharing:', err);
          // Fallback to copy to clipboard
          navigator.clipboard.writeText(shareUrl);
          alert('Link copied to clipboard!');
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <main className='min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 pt-20'>
      {/* Hero Section */}
      <div className='relative h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0 bg-gradient-to-r from-[#FE2A53]/60 to-[#38F5EF]/60 z-10'></div>
        <div className='absolute inset-0 z-0'>
          <Image
            src={article.detail_page_image_url || article.image_url}
            alt={article.title || 'article image'}
            fill
            className='object-cover'
            priority
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 40vw'
            unoptimized
          />
        </div>
        <div className='container mx-auto px-4 h-full flex flex-col justify-end pb-12 relative z-20'>
          <Badge className='mb-4 bg-[#FE2A53] hover:bg-[#FE2A53]/90 self-start text-1xl'>
            {article?.category
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (char) => char.toUpperCase())
              .charAt(0)
              .toUpperCase() +
              article?.category
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())
                .slice(1)}
          </Badge>
          <h1 className='text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl'>
            {article?.title}
          </h1>
          <div className='flex flex-wrap items-center gap-6 text-white font-bold'>
            {article?.author ? (
              <div className='flex items-center gap-2'>
                <User size={16} />
                <span>{article?.author}</span>
              </div>
            ) : (
              <></>
            )}
            {article?.date ? (
              <div className='flex items-center gap-2'>
                <Calendar size={16} />
                <span>{formatDate(article?.date)}</span>
              </div>
            ) : (
              <></>
            )}
            {article?.read_time ? (
              <div className='flex items-center gap-2'>
                <Clock size={16} />
                <span>{article?.read_time}</span>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <div className='container mx-auto px-4 py-12'>
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-12'>
          {/* Main Content */}
          <div className='lg:col-span-8'>
            {/* Breadcrumbs */}
            <div className='flex items-center text-sm text-gray-500 dark:text-white mb-8'>
              <Link
                rel='preload'
                href='/'
                className='hover:text-[#FE2A53] transition-colors'
              >
                Home
              </Link>
              <ChevronRight size={14} className='mx-2' />
              <Link
                rel='preload'
                href='/articles'
                className='hover:text-[#FE2A53] transition-colors'
              >
                Articles
              </Link>
              <ChevronRight size={14} className='mx-2' />
              <span className='text-gray-700 dark:text-secondarycolor font-medium truncate max-w-[200px]'>
                {article?.title}
              </span>
            </div>

            {/* Article Introduction */}
            <div className='mb-10'>
              <div className='prose prose-lg max-w-none'>
                {/* <div className='text-lg text-gray-900 dark:text-white leading-relaxed'>
                  {article?.description}
                </div> */}
                <div
                  className='text-lg text-gray-900 dark:text-white leading-relaxed'
                  dangerouslySetInnerHTML={{ __html: article?.description }}
                />
              </div>
            </div>

            {/* Table of Contents */}
            {article?.contents?.some((section) => section.title?.trim()) && (
              <div className='mb-10 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl'>
                <h2 className='text-xl font-bold mb-4'>Table of Contents</h2>
                <ul className='space-y-2'>
                  {article?.contents
                    ?.filter((section) => section.title?.trim())
                    ?.map((section, index) => {
                      const handleClick = (e) => {
                        e.preventDefault();
                        const element = document.getElementById(
                          `section-${index}`,
                        );
                        if (element) {
                          const yOffset = -100;
                          const y =
                            element.getBoundingClientRect().top +
                            window.pageYOffset +
                            yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      };

                      return (
                        <li key={index}>
                          <a
                            href={`#section-${index}`}
                            aria-label={`Jump to section: ${section.title}`}
                            className='text-base flex items-center hover:text-[#FE2A53] dark:hover:text-[#FE2A53] text-gray-900 dark:text-white transition-colors'
                            onClick={handleClick}
                          >
                            <span aria-hidden='true' className='mr-2'>
                              •
                            </span>
                            {section.title}
                          </a>
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}

            {/* Article Content Sections */}
            <article className='prose prose-lg max-w-none'>
              {article?.contents?.map((section, index) => (
                <section
                  key={index}
                  id={`section-${index}`}
                  className='mb-12 scroll-mt-24'
                >
                  <h2 className='text-2xl font-bold mb-4 dark:text-secondarycolor'>
                    {section.title}
                  </h2>

                  {section.image && (
                    <div className='my-6 rounded-xl overflow-hidden'>
                      <Image
                        src={section.image || '/placeholder.svg'}
                        alt={section.title || 'section image'}
                        width={800}
                        height={450}
                        unoptimized
                        className='w-full h-auto object-cover rounded-xl'
                        priority
                      />
                    </div>
                  )}

                  <div
                    className='mt-4 text-lg'
                    dangerouslySetInnerHTML={{ __html: section.description }}
                  />
                </section>
              ))}
            </article>

            {/* Tags */}
            <div className='mt-12'>
              <h3 className='text-lg font-semibold mb-4'>Topics</h3>
              <div className='flex flex-wrap gap-2'>
                {categories.map((tag, index) => (
                  <Badge
                    key={`tag-${index}`}
                    variant='outline'
                    className='px-3 py-1'
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Share Buttons */}
            <div className='mt-12 flex items-center gap-4'>
              <span className='font-medium'>Share this article:</span>
              {/* <Button
                variant='outline'
                size='icon'
                onClick={() => {
                  if (navigator.share) {
                    navigator
                      .share({
                        title: article?.title,
                        text: article?.meta_description,
                        url: shareUrl,
                      })
                      .catch((err) => {
                        console.error('Error sharing:', err);
                        // Fallback to copy to clipboard
                        navigator.clipboard.writeText(shareUrl);
                        alert('Link copied to clipboard!');
                      });
                  } else {
                    // Fallback for browsers that don't support Web Share API
                    navigator.clipboard.writeText(shareUrl);
                    alert('Link copied to clipboard!');
                  }
                }}
              >
                <Share2 size={18} />
              </Button> */}

              <Button
                variant='outline'
                size='icon'
                onClick={shareArticle}
                aria-label='Share article'
              >
                <Share2 size={18} />
              </Button>

              <Button
                variant='outline'
                size='icon'
                onClick={toggleBookmark}
                aria-label={
                  isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'
                }
                className={isBookmarked ? 'bg-gray-100' : ''}
              >
                {isBookmarked ? (
                  <BookmarkCheck size={18} className='text-[#FE2A53]' />
                ) : (
                  <Bookmark size={18} />
                )}
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={goToBookmarks}
                className='ml-auto bg-primarycolor hover:bg-secondarycolor text-white hover:text-black  dark:bg-primarycolor dark:hover:bg-secondarycolor dark:text-white dark:hover:text-black'
              >
                View Bookmarks
              </Button>
            </div>

            {article?.author ? (
              <div className='mt-12 p-6 bg-gray-50 rounded-xl shadow-xl'>
                <div className='flex flex-col sm:flex-row gap-6 items-center sm:items-start'>
                  <Image
                    // src='/placeholder.svg?height=100&width=100'
                    src={
                      article.author_image_url
                        ? article.author_image_url
                        : 'https://images.x-space.ae/Employee/earl_new.webp?height=100&width=100'
                    }
                    alt={article?.author || 'Author image'}
                    width={100}
                    height={100}
                    unoptimized
                    className='rounded-full'
                    priority
                  />
                  <div>
                    <h3 className='text-xl font-bold mb-1'>
                      {article?.author}
                    </h3>
                    <p className='text-gray-600 mb-4'>
                      Content Writer at X-Space Real Estate
                    </p>
                    <p className='text-gray-700'>
                      {article?.author} is a content writer at X-Space Real
                      Estate, specializing in creating comprehensive guides
                      about Dubai and Abu Dhabi. With extensive knowledge of the
                      UAE, they provide valuable insights for residents and
                      visitors alike.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>

          {/* Sidebar */}
          <div className='lg:col-span-4'>
            {/* Related Articles with separate scroll */}
            <div className='sticky top-24 space-y-8 mb-2'>
              {relatedArticles?.length > 0 && (
                <div>
                  <h3 className='text-xl font-bold mb-3'>Related Articles</h3>
                  <div className='h-[400px] overflow-y-auto pr-2 space-y-6 custom-scrollbar'>
                    {relatedArticles?.map((relatedArticle) => (
                      <div key={relatedArticle?._id}>
                        <Link
                          rel='preload'
                          href={`/articles/${relatedArticle?.slug}`}
                          // key={relatedArticle?.id}
                        >
                          <Card className='overflow-hidden transition-all duration-300 group border-0 bg-white dark:bg-gray-800 '>
                            <div className='relative h-40'>
                              <Image
                                src={
                                  relatedArticle.image_url || '/placeholder.svg'
                                }
                                alt={
                                  relatedArticle.title ||
                                  'Article image Related'
                                }
                                fill
                                priority
                                unoptimized
                                className='object-cover transition-transform duration-500 group-hover:scale-105'
                                sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                              />
                            </div>
                            <CardContent className='pt-4'>
                              <Badge
                                variant='outline'
                                className='mb-2 bg-[#FE2A53] hover:bg-[#FE2A53]/90 text-white'
                              >
                                {relatedArticle?.category
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase(),
                                  )}
                              </Badge>
                              <h4 className='group-hover:text-[#FE2A53] transition-colors duration-300 font-bold text-gray-900 dark:text-white'>
                                {relatedArticle?.title}
                              </h4>
                              <div className='flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-300'>
                                <Calendar size={14} />
                                <span>{formatDate(relatedArticle?.date)}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Signup */}
              <div className='mt-10 p-6 bg-gradient-to-r from-[#38F5EF]/10 to-[#FE2A53]/10 dark:from-[#38F5EF]/30 dark:to-[#FE2A53]/30 rounded-xl'>
                <h3 className='text-xl font-bold mb-4'>Stay Updated</h3>
                <p className='text-gray-600 dark:text-gray-300  mb-4'>
                  Subscribe to our newsletter for the latest market insights and
                  property listings.
                </p>
                <div className='space-y-4'>
                  <input
                    type='email'
                    placeholder='Your email address'
                    className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FE2A53]'
                  />
                  <Button className='w-full bg-primarycolor hover:bg-secondarycolor text-white hover:text-black  dark:bg-primarycolor dark:hover:bg-secondarycolor dark:text-white dark:hover:text-black'>
                    Subscribe
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Articles Button - Mobile Only */}
      <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 lg:hidden'>
        <Button
          aschild='true'
          className='shadow-lg bg-[#FE2A53] hover:bg-[#FE2A53]/90 dark:bg-secondarycolor dark:hover:bg-secondarycolor/90'
        >
          <Link
            rel='preload'
            href='/articles'
            className='flex items-center gap-2'
          >
            <ArrowLeft size={16} />
            <span>Back to Articles</span>
          </Link>
        </Button>
      </div>
    </main>
  );
}
